// SPDX-License-Identifier: BUSL-1.1

/**
 * Operators fleet view (CEO-V4 P1, NocoDB epic CEO-V4).
 *
 * One-page grid of every active agent in the caller's organization, built
 * on the existing `/agents` (unified-agents) endpoint with the new
 * derived `lastActivityAt` column. Designed for the moderator who needs
 * to spot:
 *   - Silent runners (heartbeats OK, no activity in N minutes)
 *   - Budget-exhausted agents (budget card surfaces the cap + spend)
 *   - Stale heartbeats (HeartbeatStalenessChip mirrors the watchdog
 *     thresholds in scheduled.ts)
 *
 * Composition-only: every visible primitive (DataTable,
 * HeartbeatStalenessChip, AgentHealthBadge, AgentBudgetCard, relativeTime)
 * already ships in the UI; this route wires them together for the fleet
 * view.
 *
 * Polling: 10s interval (matches the dashboard convention — see
 * dashboard-v2.tsx for the canonical refetchInterval value).
 *
 * Scope discipline: this route reads only. Mutations stay on
 * /agents/:id (Lane P3b — explicitly out of scope for P1).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { apiFetch } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table';
import { AgentListSkeleton } from '@/components/skeletons';
import { AgentHealthBadge } from '@/components/badges';
import { deriveAgentHealth } from '@/lib/agent-health';
import { HeartbeatStalenessChip } from '@/components/heartbeat-staleness-chip';
import { AgentBudgetCard } from '@/components/agent-budget-card';
import { OperatorActivityDrilldown } from '@/components/operator-activity-drilldown';
import { relativeTime } from '@/lib/format';
import {
  FleetActivityStreamProvider,
  FleetActivityStreamStatusBanner,
  FleetBlockerBanner,
  LiveActivityCell,
} from '@/components/live-activity-cell';
import { useCurrentOrg } from '@/contexts/org-context';

/* ── Types ───────────────────────────────────────────────────────────────── */

interface OperatorAgent {
  id: string;
  agentId: string;
  organizationId: string | null;
  sdk: string;
  name: string;
  version: string | null;
  status: 'idle' | 'running' | 'stopped' | 'error';
  policy: string;
  budgetMonthly: number | string | null;
  budgetSpentThisMonth: number | string | null;
  budgetAction: string | null;
  triggers: string[];
  source: string | null;
  lastHeartbeat: string | null;
  /** New derived column added by the GET /api/agents extension (CEO-V4 P1). */
  lastActivityAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * NocoDB #4789 — agent's self-declared capabilities. Sub-fields are
   * optional because older agents may not advertise the full shape; UI
   * code that reads these MUST tolerate undefined. `livePerSessionStream`
   * specifically is opt-in: agents that produce session rows AND expose a
   * per-session SSE endpoint (Anthropic Managed Agents) set it true;
   * runner-style SDKs (pi-coding-agent, aider, custom) set it false.
   */
  capabilities?: {
    sessions?: boolean;
    jobs?: boolean;
    budget?: boolean;
    policy?: boolean;
    approval?: boolean;
    rateLimit?: boolean;
    livePerSessionStream?: boolean;
  };
  /** Some API responses nest capabilities inside `config` (legacy shape). */
  config?: {
    capabilities?: OperatorAgent['capabilities'];
    [key: string]: unknown;
  };
}

/* ── Empty state ─────────────────────────────────────────────────────────── */

const EMPTY_MESSAGE =
  'No active agents — onboard a runner via `ceo runners create`.';

/* ── Cell renderers ─────────────────────────────────────────────────────── */

function AgentNameCell({ agent }: { agent: OperatorAgent }) {
  return (
    <Link
      to={`/agents/${agent.agentId}`}
      className="block transition-colors hover:text-blue-400"
    >
      <div className="font-medium text-neutral-100">{agent.name}</div>
      <div className="text-xs text-neutral-500">{agent.agentId}</div>
    </Link>
  );
}

function ActivityCell({ agent }: { agent: OperatorAgent }) {
  if (!agent.lastActivityAt) {
    return <span className="text-xs text-neutral-500">no activity yet</span>;
  }
  return (
    <span
      className="text-xs text-neutral-300"
      title={`Last evidence-of-work event at ${new Date(agent.lastActivityAt).toISOString()}`}
    >
      {relativeTime(agent.lastActivityAt)}
    </span>
  );
}

/* ── Summary row ────────────────────────────────────────────────────────── */

function FleetSummary({ agents }: { agents: OperatorAgent[] }) {
  const runningCount = agents.filter((a) => a.status === 'running').length;
  const errorCount = agents.filter((a) => a.status === 'error').length;
  const silentCount = agents.filter((a) => {
    if (!a.lastActivityAt) return false;
    const ageMin =
      (Date.now() - new Date(a.lastActivityAt).getTime()) / 60_000;
    // "Silent" mirrors the watchdog's STALL_SUSPICIOUS_SECONDS default
    // of 300s (5 min) — see scheduled.ts and HeartbeatStalenessChip.
    return ageMin >= 5;
  }).length;

  return (
    <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <SummaryCard label="Total" value={agents.length} />
      <SummaryCard label="Running" value={runningCount} tone="green" />
      <SummaryCard label="Errors" value={errorCount} tone="red" />
      <SummaryCard label="Silent (≥5m)" value={silentCount} tone="amber" />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'green' | 'red' | 'amber';
}) {
  const toneClass =
    tone === 'green'
      ? 'text-green-400'
      : tone === 'red'
        ? 'text-red-400'
        : tone === 'amber'
          ? 'text-amber-400'
          : 'text-neutral-100';
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/50 p-3">
      <p className="text-xs uppercase tracking-wide text-neutral-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

/* ── Selected-agent budget panel ────────────────────────────────────────── */

/**
 * When at least one agent in the fleet has a budget configured, surface
 * the highest-utilization one inline so the operator sees the riskiest
 * budget without leaving the page. Cards for unbudgeted agents render
 * `null` (AgentBudgetCard contract), so this stays empty when no one in
 * the fleet has a cap set.
 */
function TopBudgetAgent({ agents }: { agents: OperatorAgent[] }) {
  const ranked = agents
    .filter((a) => a.budgetMonthly != null)
    .map((a) => {
      const monthly = Number(a.budgetMonthly ?? 0);
      const spent = Number(a.budgetSpentThisMonth ?? 0);
      const pct = monthly > 0 ? spent / monthly : 0;
      return { agent: a, pct };
    })
    .sort((a, b) => b.pct - a.pct);
  const top = ranked[0];
  if (!top) return null;
  return (
    <div className="mb-4">
      <h2 className="mb-2 text-sm font-medium text-neutral-300">
        Highest-utilization budget: {top.agent.name}
      </h2>
      <AgentBudgetCard agent={top.agent} agentId={top.agent.agentId} />
    </div>
  );
}

/* ── Main route ─────────────────────────────────────────────────────────── */

export function OperatorsRoute() {
  const { currentOrg } = useCurrentOrg();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['operators', 'agents'],
    queryFn: () => apiFetch<{ data: OperatorAgent[] }>('/agents'),
    // 10s polling matches the dashboard convention so the fleet view stays
    // in sync with heartbeat cadence (runners heartbeat every 30s, so 10s
    // poll surfaces a stalled runner within one watchdog window).
    refetchInterval: 10_000,
  });

  // CEO-V4 L3 drill-down — operators can click a row's "Activity" action to
  // open the side sheet showing live + historical events for one agent
  // without leaving the fleet view. State lifts here so the sheet sits
  // outside the DataTable's render scope.
  const [drilldownAgent, setDrilldownAgent] = useState<OperatorAgent | null>(
    null,
  );

  if (isLoading) return <AgentListSkeleton />;

  if (isError) {
    return (
      <div className="rounded-md border border-red-800 bg-red-950/40 p-4 text-sm text-red-300">
        Failed to load operators:{' '}
        {error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }

  const agents = data?.data ?? [];

  return (
    <div>
      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-100">Operators</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Fleet view of active agents — heartbeats, evidence-of-work, and
            budget at a glance.
          </p>
        </div>
        <div className="text-xs text-neutral-500" data-testid="operators-poll-hint">
          polls every 10s
        </div>
      </div>

      <FleetSummary agents={agents} />
      <TopBudgetAgent agents={agents} />

      <FleetActivityStreamProvider orgId={currentOrg?.id ?? null}>
        <FleetActivityStreamStatusBanner />
        <FleetBlockerBanner
          onSelectAgent={(agentId) => {
            const match = agents.find((a) => a.agentId === agentId);
            if (match) setDrilldownAgent(match);
          }}
        />
        <DataTable
          columns={[
            {
              id: 'name',
              header: 'Agent',
              cell: ({ row }) => <AgentNameCell agent={row.original} />,
            },
            {
              id: 'health',
              header: 'Health',
              cell: ({ row }) => (
                <AgentHealthBadge health={deriveAgentHealth(row.original)} />
              ),
            },
            {
              id: 'heartbeat',
              header: 'Heartbeat',
              cell: ({ row }) => (
                <HeartbeatStalenessChip lastHeartbeat={row.original.lastHeartbeat} />
              ),
            },
            {
              id: 'activity',
              header: 'Last activity',
              cell: ({ row }) => <ActivityCell agent={row.original} />,
            },
            {
              id: 'liveActivity',
              header: 'Live',
              cell: ({ row }) => (
                <LiveActivityCell agentId={row.original.agentId} />
              ),
            },
            {
              accessorKey: 'sdk',
              header: 'SDK',
              cell: ({ row }) => (
                <span className="text-xs uppercase text-neutral-400">
                  {row.original.sdk}
                </span>
              ),
            },
            {
              accessorKey: 'policy',
              header: 'Policy',
              cell: ({ row }) => (
                <span className="text-sm text-neutral-400">{row.original.policy}</span>
              ),
            },
            {
              id: 'drill',
              header: '',
              cell: ({ row }) => (
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setDrilldownAgent(row.original)}
                  aria-label={`View activity for ${row.original.name}`}
                  data-testid={`drilldown-${row.original.agentId}`}
                >
                  Activity
                </Button>
              ),
            },
          ]}
          data={agents}
          emptyMessage={EMPTY_MESSAGE}
        />
      </FleetActivityStreamProvider>

      <OperatorActivityDrilldown
        agent={
          drilldownAgent
            ? {
                agentId: drilldownAgent.agentId,
                name: drilldownAgent.name,
                status: drilldownAgent.status,
                lastHeartbeat: drilldownAgent.lastHeartbeat,
                // NocoDB #4789 — flatten the capability shape so the
                // drilldown's LiveSection can pick LiveStreamPanel vs
                // AgentFleetActivity without re-querying.  Prefer the
                // top-level field; fall back to the legacy `config.
                // capabilities` shape some older agents still return.
                capabilities:
                  drilldownAgent.capabilities ??
                  drilldownAgent.config?.capabilities,
              }
            : null
        }
        open={drilldownAgent != null}
        onClose={() => setDrilldownAgent(null)}
      />
    </div>
  );
}
